import moment from 'moment';
import 'moment/locale/fr';
import 'moment/locale/ar-ma';
import 'moment/locale/es';
import 'moment/locale/de';
import { Dispatch, SetStateAction } from 'react';
import store from 'store';
import { AxiosProgressEvent } from 'axios';
export function formatDate(date: Date, format?: string, separator?: string) {
  format = format ?? 'dd-MM-yyyy';
  // Read at call time: a module-load snapshot never sees a mid-session
  // language switch, which left moment pinned to whatever was active on first
  // import and silently defeated every locale below.
  const language = store.getState()?.language?.language;
  let lang = language ?? process.env.NEXT_PUBLIC_DEFAULT_LANGUAGE ?? 'fr';
  if (lang === 'ar') {
    lang = 'ar-ma';
  }
  // German writes dates dd.MM.yyyy; every other supported locale keeps the
  // product-wide dash. Callers passing an explicit separator still win.
  separator = separator ?? (lang === 'de' ? '.' : '-');

  moment.locale(lang);
  const dateObj = moment(date);
  const year = dateObj.year();
  const month = dateObj.format('MM');
  const day = dateObj.format('DD');
  const monthShort = dateObj.format('MMM');
  const time = dateObj.format('HH:mm:ss');
  const nameOfDay = dateObj.format('dddd');
  const monthFull = dateObj.format('MMMM');

  if (format === 'yyyy-MM-dd') {
    return `${year}${separator}${month}${separator}${day}`;
  } else if (format === 'dd-MM') {
    return `${day}${separator}${month}`;
  } else if (format === 'dd-MMM') {
    return `${day}${separator}${monthShort}`;
  } else if (format === 'dd-MMM-yyyy') {
    return `${day}${separator}${monthShort}${separator}${year}`;
  } else if (format === 'MM-yyyy') {
    return `${month}${separator}${year}`;
  } else if (format === 'dd') {
    return `${day}`;
  } else if (format === 'MM') {
    return `${month}`;
  } else if (format === 'MMM') {
    return monthShort;
  } else if (format === 'dd/MM/yyyy HH:mm:ss') {
    return `${day}/${month}/${year} ${time}`;
  } else if (format === 'ddd DD MMMM') {
    return `${nameOfDay}${separator}${day}${separator}${monthFull}`;
  }

  return `${day}${separator}${month}${separator}${year}`;
}

/**
 * BCP-47 tag for the active UI language, for Intl number/currency formatting.
 *
 * Intl needs the region to pick separators: `de-DE` gives 1.234,56 while
 * `fr-FR` gives 1 234,56. Every non-German language keeps mapping to `fr-FR`,
 * which is the format this product has always shown them — English included,
 * deliberately. Only German diverges, so only German is mapped away.
 */
export function getIntlLocale(): string {
  const language =
    store.getState()?.language?.language ??
    process.env.NEXT_PUBLIC_DEFAULT_LANGUAGE ??
    'fr';
  return String(language).toLowerCase() === 'de' ? 'de-DE' : 'fr-FR';
}

export const validatePdfOrImg = (fileName: string): boolean => {
  const ext = fileName.split('.').pop()?.toLowerCase() as string;
  const extImg = ['png', 'jpg', 'jpeg', 'jfif'];
  const extPdf = ['pdf'];
  return extImg.includes(ext) || extPdf.includes(ext);
};

export function getIconByExtension(filename: string) {
  const filenameArray = filename.split('.');
  const ext = filenameArray[filenameArray.length - 1].toLowerCase();
  let iconName = 'doc-file.svg';

  if (ext === 'pdf') {
    iconName = 'doc-pdf.svg';
  } else if (ext === 'xsl' || ext === 'xlsx') {
    iconName = 'doc-excel.svg';
  } else if (ext === 'docx') {
    iconName = 'doc-word.svg';
  } else if (ext === 'png' || ext === 'jpg' || ext === 'jpeg') {
    iconName = 'doc-img.svg';
  } else if (ext === 'ppt' || ext === 'pptx') {
    iconName = 'doc-ppt.svg';
  } else if (ext === 'zip' || ext === 'rar') {
    iconName = 'doc-zip.svg';
  } else if (ext === 'txt') {
    iconName = 'doc-file.svg';
  }

  return iconName;
}
export const checkFileType = (fileName: string | null) => {
  if (!fileName) return null;

  const lowerName = fileName.toLowerCase();

  const isImage = /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(lowerName);
  const isPdf = /\.pdf$/i.test(lowerName);

  if (isImage) return 'image';
  if (isPdf) return 'pdf';

  return false;
};

export const onUploadProgress =
  (setProgress: Dispatch<SetStateAction<number>>) =>
  (progressEvent: AxiosProgressEvent | ProgressEvent<XMLHttpRequest>) => {
    let totalLength: number | undefined;
    let loaded: number;

    if ('total' in progressEvent) {
      totalLength = progressEvent.total ?? undefined;
      loaded = progressEvent.loaded;
    } else {
      const xhrEvent =
        progressEvent as unknown as ProgressEvent<XMLHttpRequest>;
      totalLength = xhrEvent.lengthComputable
        ? xhrEvent.total
        : Number(xhrEvent.target?.getResponseHeader('content-length')) ||
          Number(
            xhrEvent.target?.getResponseHeader('x-decompressed-content-length'),
          ) ||
          undefined;
      loaded = xhrEvent.loaded;
    }

    if (totalLength) {
      const progressPercentage = Math.round((loaded * 100) / totalLength);
      setProgress(progressPercentage);
    }
  };

export function appendToFormData(values: unknown): FormData {
  const formData = new FormData();

  function appendValue(key: string, value: any) {
    if (value === null || value === undefined) {
      return;
    }

    if (value instanceof Blob) {
      formData.append(key, value);
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (item instanceof Blob) {
          formData.append(key, item);
        } else {
          appendValue(`${key}[${index}]`, item);
        }
      });
      return;
    }

    if (typeof value === 'object') {
      Object.entries(value).forEach(([subKey, subValue]) => {
        appendValue(`${key}[${subKey}]`, subValue);
      });
      return;
    }

    formData.append(key, value.toString());
  }

  // Process top-level values
  Object.entries(values).forEach(([key, value]) => {
    appendValue(key, value);
  });

  return formData;
}

export function truncateText(text: string, numChars: number) {
  if (text.length <= numChars) {
    return text;
  }
  return text.slice(0, numChars) + '...';
}

export const downloadFromBase64 = (base64Data: string, fileName: string) => {
  const link = document.createElement('a');
  link.href = 'data:application/vnd.ms-excel;base64,' + base64Data;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadExcelBuffer = (
  response: ArrayBuffer,
  fileName: string,
): void => {
  const blob = new Blob([response], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/** Save an arraybuffer API response as a file — the generic behind downloadExcelBuffer, for the
 *  other content types (PDF, ZIP). */
export const downloadBlob = (
  response: ArrayBuffer,
  mimeType: string,
  fileName: string,
): void => {
  const blob = new Blob([response], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const downloadPdfBuffer = (
  response: ArrayBuffer,
  fileName: string,
): void => downloadBlob(response, 'application/pdf', fileName);

export const handlePreviewFileImage = (
  fileInput: HTMLInputElement,
  imageClass: string,
): void => {
  if (!fileInput) return;

  const image = document.querySelector(
    `.${imageClass}`,
  ) as HTMLImageElement | null;

  if (!image) {
    console.error('Image element not found.');
    return;
  }

  const file = fileInput?.files?.[0];

  if (file && file.type.startsWith('image/')) {
    const blobUrl = URL.createObjectURL(file);
    image.src = blobUrl;
  } else {
    console.error('Selected file is not an image.');
  }
};
type LowercaseFirstChar<S extends string> = S extends `${infer F}${infer R}`
  ? `${Lowercase<F>}${R}`
  : S;

type LowercaseFirstCharKeys<T> =
  T extends Array<infer U>
    ? Array<LowercaseFirstCharKeys<U>>
    : T extends object
      ? {
          [K in keyof T as K extends string
            ? LowercaseFirstChar<K>
            : never]: LowercaseFirstCharKeys<T[K]>;
        }
      : T;

export const keysToLowerCaseFirstChar = <T>(
  obj: T,
): LowercaseFirstCharKeys<T> => {
  if (Array.isArray(obj)) {
    return obj.map((item) =>
      keysToLowerCaseFirstChar(item),
    ) as LowercaseFirstCharKeys<T>;
  } else if (obj !== null && typeof obj === 'object') {
    const result: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(obj)) {
      const newKey = key.charAt(0).toLowerCase() + key.slice(1);
      result[newKey] = keysToLowerCaseFirstChar(value);
    }

    return result as LowercaseFirstCharKeys<T>;
  }

  return obj as LowercaseFirstCharKeys<T>;
};

export const hexToRgba = (
  hex: string,
  opacity: number | null = null,
): string => {
  hex = hex?.replace(/^#/, '');

  let r,
    g,
    b,
    a = 1;

  if (hex?.length === 8) {
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
    a = parseInt(hex.substring(6, 8), 16) / 255;
  } else if (hex?.length === 6) {
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
  } else {
    return '#fff';
  }

  if (opacity !== null) {
    a = Math.max(0, Math.min(1, opacity));
  }

  return `rgba(${r}, ${g}, ${b}, ${a.toFixed(2)})`;
};

export const downloadFromArrayBuffer = (
  arrayBuffer: ArrayBuffer,
  fileName: string,
): void => {
  const fileExtension = fileName.split('.').pop()?.toLowerCase();
  const mimeTypes: { [key: string]: string } = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    txt: 'text/plain',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    zip: 'application/zip',
    rar: 'application/x-rar-compressed',
  };

  const mimeType = mimeTypes[fileExtension || ''] || 'application/octet-stream';

  const blob = new Blob([arrayBuffer], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export function getStatusColorText(color: string): string {
  const colorClassMap: { [key: string]: string } = {
    '#FFA500': 'warning',
    '#EFB469': 'warning',
    '#2196F3': 'info',
    '#7CCCE5': 'info',
    '#00FF00': 'success',
    '#2B7A55': 'success',
    '#FF0000': 'danger',
    '#E87C70': 'danger',
    '#264653': 'success',
    '#2A9D8F': 'success',
    '#E9C46A': 'success',
    '#F4A261': 'success',
    '#E76F51': 'danger',
    '#D62828': 'danger',
    '#023E8A': 'info',
    '#0077B6': 'info',
    '#0096C7': 'info',
    '#00B4D8': 'info',
    '#48CAE4': 'success',
    '#A6C7EA': 'info',
    '#1565BB': 'info',
    '#89BBDE': 'info',
    '#2D72A3': 'info',
    '#6899AA': 'info',
    '#3E5D68': 'info',
    '#1e89db': 'info',
    '#A9DBD2': 'success',
    '#3D9081': 'success',
    '#8FCFB4': 'success',
    '#3E926E': 'success',
    '#88BBAA': 'success',
    '#54917C': 'success',
    '#BBDCAD': 'success',
    '#66AD48': 'success',
    '#BBCAB3': 'success',
    '#7B986B': 'success',
    '#F9F18C': 'warning',
    '#FCEE3A': 'warning',
    '#DFE88D': 'warning',
    '#AFBD2A': 'warning',
    '#F1ED8C': 'warning',
    '#DCD522': 'warning',
    '#F4F0B0': 'warning',
    '#E1D959': 'warning',
    '#F8B445': 'warning',
    '#CBAC8F': 'danger',
    '#A4784F': 'danger',
    '#FBDDDD': 'danger',
    '#EA7373': 'danger',
    '#F8B2BD': 'danger',
    '#EF576F': 'danger',
    '#B7C4CF': 'info',
    '#607B93': 'info',
    '#F9A485': 'danger',
    '#F57141': 'danger',
    '#EFA695': 'danger',
    '#E46F54': 'danger',
    '#FDBD7D': 'danger',
    '#FE9731': 'danger',
    '#CEB3D6': 'warning',
    '#A266B4': 'warning',
    '#BABD42': 'danger',
    '#8C8E36': 'danger',
    '#282A3A': 'danger',
    '#7279B9': 'info',
    '#F24C4C': 'danger',
    '#E75151': 'danger',
    '#FA7070': 'danger',
    '#EA3939': 'danger',
    '#7D9D9C': 'info',
    '#425656': 'info',
    '#2A3990': 'info',
    '#A1AADC': 'info',
    '#3B185F': 'danger',
    '#A978DB': 'warning',
    '#C060A1': 'danger',
    '#873A6E': 'danger',
    '#5F9DF7': 'info',
    '#375D95': 'info',
    '#DFBB9D': 'danger',
    '#BA7943': 'danger',
    '#18978F': 'success',
    '#63EFE7': 'info',
    '#1363DF': 'info',
    '#AAC4FF': 'info',
    '#7858A6': 'info',
    '#BF9FEE': 'info',
    '#1D5C63': 'success',
    '#60C5D0': 'info',
    '#774360': 'danger',
    '#D17FAD': 'danger',
    '#68A7AD': 'success',
    '#1F7178': 'success',
    '#863A6F': 'danger',
    '#E952BC': 'info',
    '#51557E': 'info',
    '#7981D3': 'info',
    '#E4AEC5': 'info',
    '#B73A70': 'danger',
    '#D9CB50': 'warning',
    '#716711': 'success',
    '#2C7955': 'success',
  };
  return colorClassMap[color] || 'danger';
}

export const normalizeEmptyNumericValues = <T extends Record<string, any>>(
  values: T,
): T => {
  const normalized: any = Array.isArray(values) ? [] : {};

  for (const [key, value] of Object.entries(values)) {
    if (value !== null && typeof value === 'object') {
      normalized[key] = normalizeEmptyNumericValues(value);
    } else if (value === '') {
      normalized[key] = null;
    } else {
      normalized[key] = value;
    }
  }

  return normalized;
};

export const getTabNameFromType = (type?: number): string => {
  switch (type) {
    case 2:
      return 'Department';
    case 3:
      return 'Function';
    case 4:
      return 'Employee';
    case 5:
      return 'Site';
    case 1:
      return 'Society';
    case 0:
      return 'Public';
    default:
      return 'Public';
  }
};

export const isPreviewable = (fileName: string): boolean => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  return ext === 'pdf' || ['jpg', 'jpeg', 'png', 'gif'].includes(ext || '');
};
